import React, {useEffect} from 'react';

import {Box, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';

import icons from 'assets/icons';
import {PremiumLock, Text} from 'components';
import AboInfo from 'layout/components/toolbar/components/AboInfo';
import {ColorTool} from 'layout/components/toolbar/components/index';
import {useLicenseManager} from 'module/user';
import {usePrefsStore} from 'store';

const styles = {
    toolButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        cursor: 'pointer',
        width: {
            mobile: '50px',
            desktop: '70px',
        },
        height: {
            mobile: '50px',
            desktop: '70px',
        },
        margin: '2px',
        backgroundColor: 'grey.darkest',
        borderRadius: '50%',
    },
    selected: {
        backgroundColor: 'grey.dark',
    },
    titleHolder: {
        marginLeft: '0.5%',
    }
};

const SvgIcons = {
    'pencil': icons.Pencil,
    'arrow': icons.Arrow,
    'triangle': icons.Triangle,
    'arrowDashed': icons.ArrowDashed,
    'circle': icons.Circle,
    'rect': icons.Rectangle,
    'futbol': icons.SoccerBall,
    'cone': icons.ConeLarge,
    'coneSmall': icons.ConeSmall,
    'pole': icons.Pole,
    'ladder': icons.Ladder,
    'text': icons.Text,
    'down': icons.ArrowDownward,
    'up': icons.ArrowUpward,
};

const shapes = [
    'pencil', 'arrow', 'triangle', 'arrowDashed', 'circle', 'rect'
];

const objects = [
    'futbol', 'cone', 'coneSmall', 'pole', 'ladder'
];

const Button = ({name, selected, onClick, isPremium}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
    const SvgIcon = SvgIcons[name];

    let size = isMobile ? '25px' : '35px';
    let sizeSelected = isMobile ? '32px' : '45px';

    return <Box
        sx={[styles.toolButton, selected && styles.selected]}
        onClick={isPremium ? () => console.log('LINK TO PREMIUM PAGE') : onClick}
    >
        {isPremium && <PremiumLock small />}
        <SvgIcon style={{
            fill: '#ffffff',
            display: 'block',
            width: selected ? sizeSelected : size,
            height: selected ? sizeSelected : size,
        }}/>
    </Box>;
};

const DrawingTool = ({onSelect}) => {
    const prefs = usePrefsStore((state) => state.prefs.draw);
    const setPref = usePrefsStore((state) => state.setPref);
    const {hasAccess, hasFullAccess} = useLicenseManager();

    const onClick = (type, name) => {
        setPref('draw', {type, [type]: name});
        onSelect('draw');
    };

    useEffect(() => {
        const currentType = prefs?.type;
        const currentName = prefs?.[currentType];
        const allItems = [
            ...shapes.map(name => ({type: 'shape', name})),
            ...objects.map(name => ({type: 'object', name})),
        ];
        const current = allItems.find(i => i.type === currentType && i.name === currentName);
        if (current && !hasAccess('tools', current.name).access) {
            const first = allItems.find(i => hasAccess('tools', i.name).access);
            if (first) setPref('draw', {type: first.type, [first.type]: first.name});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        <Box>
            <Typography sx={styles.titleHolder} variant="h2" color="white.main" mb={3}><Text>board.toolbar.draw</Text></Typography>
            <Typography sx={styles.titleHolder} variant="h5" color="grey.mainlight" mb={1}><Text>board.toolbar.shapes</Text></Typography>
        </Box>

        <Stack direction="row" flexWrap="wrap" useFlexGap={true}>
            {shapes.map((name, index) => {
                const shapesAccess = hasAccess('tools', name).access;
                return (
                    <Button
                        key={index}
                        name={name}
                        selected={prefs?.type === 'shape' && prefs?.['shape'] === name}
                        onClick={() => onClick('shape', name)}
                        isPremium={!shapesAccess}
                    />
                );
            })}
        </Stack>

        <Box mt={4}>
            <Typography sx={styles.titleHolder} variant="h5" color="grey.mainlight" mb={1}><Text>board.toolbar.objects</Text></Typography>
        </Box>

        <Stack direction="row" flexWrap="wrap" useFlexGap={true}>
            {objects.map((name, index) => {
                const objectsAccess = hasAccess('tools', name).access;
                return (
                    <Button
                        key={index}
                        name={name}
                        selected={prefs?.type === 'object' && prefs?.['object'] === name}
                        onClick={() => onClick('object', name)}
                        isPremium={!objectsAccess}
                    />
                );
            })}
        </Stack>

        <ColorTool />

        {!hasFullAccess('tools') && <AboInfo /> }

    </>;
};

export default DrawingTool;